/**
 * QMP-FE-2 (F2) — THE EDITOR OF STEP 2 (2a / 2b / 2c), held by the wizard page
 * so an unsaved subarea stays unsaved — and says so on the stepper — while the
 * person is on another step. QMP-FE-4: the same editor holds steps 4
 * (`ncr`, `rfi`, `visibility`) and 5 (`ai`); QMP-FE-3 adds step 3
 * (`handover`, `punch_list`); `steps[2|3|4|5]` is what each step's circle on
 * the stepper shows.
 *
 * `punch_list` is the one subarea read against another: its "per handover"
 * rule needs the package's HANDOVER PATH, so it is built and edited with
 * `withHandover(ctx, states.handover)` (`ctxOf`), and re-read whenever the
 * handover path changes.
 *
 * One state per subarea, `{general, rows}` (`lib/qmpRules`), read from the plan
 * through the grammar. When the plan is re-read, a subarea with no local edits
 * is read again; one with local edits keeps them and is fitted to the new
 * structure.
 *
 * SAVE = `PUT /qmp/packages/rules/{section}` with every live package, then
 * `reload()` — never local optimism. A refusal is kept for the screen:
 * 422 with a `qwp_id` belongs to that package's row; 403 / other 422 to the
 * subarea; 503 `QMP_UNREADABLE` offers to try again.
 *
 * GOVERNANCE IS THE SERVER'S. `can_govern` from the package list only decides
 * whether editors are drawn (as step 1); the 403 is what decides.
 *
 * QMP-FE-7 — THE CHOICES ARE THE SERVER'S (QMP-BE-2, D22). The standard VALUES
 * are `GET /qmp.standard_rules`; QMP-FE-9 — the CHOICES of every field
 * (`standard_options`) and the sub-steps with their titles (`standard_steps`)
 * come with the same read (QMP-BE-3), so whoever can read the plan can read
 * them and there is no second request. Until the plan has served them there is
 * nothing to draw a select with: `ready` is false and the page shows its
 * loading state — never an empty select.
 *
 * "RESTORE STANDARD RULES" IS THE SERVER'S (`POST /qmp/draft/restore-standard`,
 * one sub-step, by its code): it writes the standard fitted to every package,
 * and its answer is handled as a save's — the subarea is re-read from the
 * plan (`reload()`), its refusal drawn where a save's would be. Nothing is
 * reset locally.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  getProjectCompanies, listQwps, saveQmpPackageRules, restoreQmpStandard,
} from '../lib/api';
import { apiErrorMessage } from '../pages/TestRegisterPage';
import {
  STEP2_SECTIONS, STEP3_SECTIONS, STEP4_SECTIONS, STEP5_SECTIONS, RULE_SECTIONS, canonical,
  initSection, carrySection, isDirty, buildBody, sectionSaved, needsReviewOf,
  withHandover, sectionBlocker,
} from '../lib/qmpRules';

const STEP_SECTIONS = {
  2: STEP2_SECTIONS, 3: STEP3_SECTIONS, 4: STEP4_SECTIONS, 5: STEP5_SECTIONS,
};

export const SAVE_FALLBACK = 'Could not save these rules';
export const RESTORED = 'Standard rules restored for this step.';

export function describeSaveError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  const message = apiErrorMessage(err, SAVE_FALLBACK);
  if (status === 422 && detail && typeof detail === 'object' && detail.qwp_id) {
    return { qwpId: detail.qwp_id, message, field: detail.field, reason: detail.reason };
  }
  return { message, retry: status === 503 };
}

export default function useQmpRuleEditor(projectId, qmp) {
  const [companies, setCompanies] = useState([]);
  const [canGovern, setCanGovern] = useState(true);
  const [states, setStates] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(null);
  const resetNext = useRef(new Set());
  const prevCtx = useRef(null);
  const servedOptions = qmp.standard_options;
  const servedSteps = qmp.standard_steps;

  useEffect(() => {
    let cancelled = false;
    Promise.resolve(getProjectCompanies(projectId))
      .then((rows) => { if (!cancelled) setCompanies(Array.isArray(rows) ? rows : []); })
      .catch(() => {});
    Promise.resolve(listQwps(projectId))
      .then((r) => {
        const rows = Array.isArray(r?.data) ? r.data : [];
        if (!cancelled && rows.length) setCanGovern(rows.some((q) => q.can_govern));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [projectId]);

  const live = companies.filter((c) => c && c.id && !c.deleted_at);
  const signature = canonical({
    s: qmp.structure, p: qmp.packages, r: qmp.standard_rules, c: live.map((c) => c.id),
  });
  // Keyed on the canonical form of what was read, so a re-render that reads
  // the same plan keeps the same context (and the editor its edits).
  const ctx = useMemo(() => {
    const d = JSON.parse(signature);
    return {
      structure: d.s || [],
      packages: d.p || {},
      standardRules: d.r || {},
      liveCompanyIds: new Set(d.c || []),
    };
  }, [signature]);

  useEffect(() => {
    const before = prevCtx.current;
    // Read now: the updater below may run after this effect has returned.
    const reset = new Set(resetNext.current);
    resetNext.current.clear();
    setStates((prev) => {
      const next = {};
      RULE_SECTIONS.forEach((s) => {
        // `punch_list` is read against the path `handover` holds, and
        // `RULE_SECTIONS` puts `handover` first, so it is already here.
        const c = s === 'punch_list' ? withHandover(ctx, next.handover) : ctx;
        const local = prev[s];
        const keep = local && before && !reset.has(s) && isDirty(s, local, before);
        next[s] = keep ? carrySection(s, local, c) : initSection(s, c);
      });
      return next;
    });
    prevCtx.current = ctx;
  }, [ctx]);

  // Editing the handover path can take a package's second handover away, so
  // `punch_list` is re-read against the new path in the same update.
  const update = useCallback((section, fn) => {
    setStates((prev) => {
      if (!prev[section]) return prev;
      const next = { ...prev, [section]: fn(prev[section]) };
      if (section === 'handover' && next.punch_list) {
        next.punch_list = carrySection(
          'punch_list', next.punch_list, withHandover(ctx, next.handover),
        );
      }
      return next;
    });
  }, [ctx]);

  const save = useCallback(async (section) => {
    const state = states[section];
    if (!state) return false;
    // The screen does not send a body it can already see is incomplete
    // (mockup `plInvalid`); the server refuses the same thing anyway.
    const stop = sectionBlocker(section, state, ctx.structure);
    if (stop) {
      setErrors((e) => ({ ...e, [section]: { message: stop } }));
      return false;
    }
    setSaving(section);
    setErrors((e) => ({ ...e, [section]: null }));
    let ok = false;
    try {
      await saveQmpPackageRules(projectId, section, buildBody(state, ctx.structure));
      ok = true;
      resetNext.current.add(section);
    } catch (err) {
      setErrors((e) => ({ ...e, [section]: describeSaveError(err) }));
    }
    await qmp.reload();
    setSaving(null);
    return ok;
  }, [states, ctx, projectId, qmp]);

  const stepOf = useMemo(() => {
    const out = {};
    (servedSteps || []).forEach((x) => { out[x.section] = x.step; });
    return out;
  }, [servedSteps]);

  const restore = useCallback(async (section) => {
    setSaving(section);
    setErrors((e) => ({ ...e, [section]: null }));
    let ok = false;
    try {
      await restoreQmpStandard(projectId, stepOf[section] || section);
      ok = true;
      resetNext.current.add(section);
      toast.success(RESTORED);
    } catch (err) {
      // "Try again" re-sends a SAVE; a refused restore is asked again from
      // its own link instead.
      setErrors((e) => ({ ...e, [section]: { ...describeSaveError(err), retry: false } }));
    }
    await qmp.reload();
    setSaving(null);
    return ok;
  }, [stepOf, projectId, qmp]);

  const status = {};
  RULE_SECTIONS.forEach((s) => {
    const review = needsReviewOf(s, ctx.structure, ctx.packages);
    status[s] = {
      saved: sectionSaved(s, ctx.structure, ctx.packages),
      dirty: Boolean(canGovern && states[s] && isDirty(s, states[s], ctx)),
      review,
      attn: Object.keys(review).length > 0,
    };
  });
  const steps = {};
  Object.entries(STEP_SECTIONS).forEach(([k, sections]) => {
    const all = sections.map((s) => status[s]);
    steps[k] = {
      attn: all.some((x) => x.attn),
      dirty: all.some((x) => x.dirty),
      saved: all.every((x) => x.saved),
    };
  });

  const titles = {};
  (servedSteps || []).forEach((x) => { titles[x.section] = x.title; });

  return {
    ctx, companies: live, canGovern, states, update, save, restore, saving, errors, status, steps,
    /** The served choices (`standard_options`), the sub-step titles and codes
     *  (`standard_steps`); `ready` once the plan has served both. */
    ready: Boolean(servedOptions && servedSteps),
    options: servedOptions || {},
    titles,
    stepOf,
    /** The context a subarea is edited against — `punch_list` needs the path
     *  `handover` holds; every other subarea is `ctx` itself. */
    ctxOf: (s) => (s === 'punch_list' ? withHandover(ctx, states.handover) : ctx),
    /** The plan could not be read (e.g. 503 QMP_UNREADABLE): say so, offer to read again. */
    loadError: qmp.error,
    reload: qmp.reload,
    /** Continue saves only what the server does not hold yet. */
    needsSave: (s) => Boolean(canGovern && (!status[s].saved || status[s].dirty)),
  };
}
